// Geometry helpers and shared constants for the flow builder canvas.

export const BLOCK_WIDTH = 230;
export const DEFAULT_BLOCK_HEIGHT = 92;
export const CANVAS_EXTENT = 6000;
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 2;

// Creates a reasonably unique client-side block id.
export const makeBlockId = () =>
  `blk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export const clampZoom = zoom => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));

// Converts viewport client coordinates into canvas (untransformed) coordinates.
export const clientToCanvas = (clientX, clientY, rect, pan, zoom) => ({
  x: (clientX - rect.left - pan.x) / zoom,
  y: (clientY - rect.top - pan.y) / zoom,
});

// Input port sits at the top center of a block.
export const getInputPoint = block => ({
  x: block.position.x + BLOCK_WIDTH / 2,
  y: block.position.y,
});

// Horizontal position of the named outputs of a branching block, as a fraction
// of the block width.
const BRANCH_OUTPUT_RATIOS = { yes: 0.28, no: 0.72 };

// Output port sits at the bottom of a block (height is measured at runtime).
// Branching blocks have one port per named output instead of a single centered one.
export const getOutputPoint = (block, blockSizes, branch = null) => {
  const ratio = branch && BRANCH_OUTPUT_RATIOS[branch] != null ? BRANCH_OUTPUT_RATIOS[branch] : 0.5;
  return {
    x: block.position.x + BLOCK_WIDTH * ratio,
    y: block.position.y + (blockSizes[block.blockId] || DEFAULT_BLOCK_HEIGHT),
  };
};

export const getBranchOutputRatio = branch =>
  branch && BRANCH_OUTPUT_RATIOS[branch] != null ? BRANCH_OUTPUT_RATIOS[branch] : 0.5;

// Smooth vertical cubic bezier between two connection points.
export const buildBezierPath = (from, to) => {
  const bend = Math.max(40, Math.abs(to.y - from.y) / 2);
  return `M ${from.x} ${from.y} C ${from.x} ${from.y + bend}, ${to.x} ${to.y - bend}, ${to.x} ${to.y}`;
};

// Includes the branch so the two outputs of a split stay distinguishable even
// when both point at the same target block.
export const connectionKey = connection =>
  `${connection.from}:${connection.branch || ''}->${connection.to}`;

// Finds a spot that does not visually overlap existing blocks (cascades downwards).
export const findFreePosition = (blocks, start) => {
  const position = { x: Math.round(start.x), y: Math.round(start.y) };
  const overlaps = () =>
    blocks.some(
      block =>
        Math.abs(block.position.x - position.x) < BLOCK_WIDTH + 30 &&
        Math.abs(block.position.y - position.y) < 130
    );
  let guard = 0;
  while (overlaps() && guard < 200) {
    position.y += 40;
    guard += 1;
  }
  return position;
};
